if (!customElements.get('restock-notify')) {
  customElements.define(
    'restock-notify',
    class RestockNotify extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('[data-restock-toggle]');
        this.panel = this.querySelector('[data-restock-panel]');
        this.form = this.querySelector('[data-restock-form]');
        this.status = this.querySelector('[data-restock-status]');
        this.subscribedMessage = this.querySelector('[data-restock-subscribed-message]');
        this.alreadySubscribedMessage = this.querySelector('[data-restock-already-subscribed-message]');
        this.loginLink = this.querySelector('[data-restock-login]');
        this.switchAccountLink = this.querySelector('[data-restock-switch-account]');
        this.promptAction = this.querySelector('[data-restock-prompt-action]');
        this.emailNote = this.querySelector('[data-restock-email-note]');
        this.variantInput = this.querySelector('[data-restock-variant-id]');
        this.skuInput = this.querySelector('[data-restock-sku]');
        this.variantIndex = this.getVariantIndex();
        this.activeVariantId = '';

        this.onToggleClick = this.onToggleClick.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onOptionValueChange = this.onOptionValueChange.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);

        if (this.toggle) this.toggle.addEventListener('click', this.onToggleClick);
        if (this.form) this.form.addEventListener('submit', this.onSubmit);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeOptionValueChange = subscribe(
            PUB_SUB_EVENTS.optionValueSelectionChange,
            this.onOptionValueChange
          );
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }

        this.setInitialState();

        this.handleReturnMarker();
      }

      disconnectedCallback() {
        if (this.unsubscribeOptionValueChange) this.unsubscribeOptionValueChange();
        if (this.unsubscribeVariantChange) this.unsubscribeVariantChange();
      }

      onOptionValueChange(event) {
        if (!this.closest('product-info')?.contains(event.data.event.target)) return;
        if (this.dataset.hasMultipleVariants !== 'true') return;
        this.resetSubscriptionState();
        if (this.isExactVariantOnly()) {
          this.setHiddenState();
          return;
        }
        if (this.isPickAnOptionEnabled()) {
          this.updatePickAnOptionState();
        } else {
          this.setPromptState();
        }
      }

      onVariantChange(event) {
        if (event.data.sectionId !== this.dataset.sectionId) return;
        const variant = event.data.variant;
        this.resetSubscriptionState();
        if (this.isExactVariantOnly()) {
          if (variant?.available === false && this.getOptionSelection().complete) {
            this.setActiveState(variant);
          } else {
            this.setHiddenState();
          }
          return;
        }
        if (this.isPickAnOptionEnabled()) {
          this.updatePickAnOptionState(variant);
          return;
        }
        if (!variant || variant.available) {
          this.setPromptState();
          return;
        }
        this.setActiveState(variant);
      }

      setInitialState() {
        if (this.isExactVariantOnly()) {
          if (this.dataset.initialActive === 'true') {
            this.preserveInitialActiveState();
          } else {
            this.setHiddenState();
          }
          return;
        }

        if (!this.isPickAnOptionEnabled()) {
          if (this.dataset.initialActive === 'true') {
            this.preserveInitialActiveState();
          } else {
            this.setPromptState();
          }
          return;
        }

        const selection = this.getOptionSelection();
        if (selection.selectedCount === 0) {
          this.setHiddenState();
        } else if (selection.complete && this.dataset.initialActive === 'true') {
          this.preserveInitialActiveState();
        } else if (selection.complete || this.hasUnavailableMatch(selection.values)) {
          this.setPromptState();
        } else {
          this.setHiddenState();
        }
      }

      updatePickAnOptionState(variant = null) {
        const selection = this.getOptionSelection();

        if (selection.selectedCount === 0) {
          this.setHiddenState();
          return;
        }

        if (!selection.complete) {
          if (this.hasUnavailableMatch(selection.values)) {
            this.setPromptState();
          } else {
            this.setHiddenState();
          }
          return;
        }

        if (variant && variant.available === false) {
          this.setActiveState(variant);
          return;
        }

        if (this.hasUnavailablePartialMatch(selection.values)) {
          this.setPromptState();
        } else {
          this.setHiddenState();
        }
      }

      isExactVariantOnly() {
        return this.dataset.displayMode === 'exact_variant';
      }

      isPickAnOptionEnabled() {
        return (
          this.dataset.hasMultipleVariants === 'true' &&
          this.getVariantSelects()?.dataset.pickAnOption === 'true'
        );
      }

      getVariantSelects() {
        return this.closest('product-info')?.querySelector('variant-selects');
      }

      getOptionSelection() {
        const groups = Array.from(
          this.getVariantSelects()?.querySelectorAll('fieldset, .product-form__input--dropdown') || []
        );
        const values = groups.map((group) => {
          const fieldsetValue = group.matches('fieldset') ? group.querySelector('input:checked')?.value : null;
          if (fieldsetValue != null) return fieldsetValue;

          const select = group.querySelector('select');
          return select && select.selectedIndex !== -1 ? select.value : null;
        });
        const selectedCount = values.filter((value) => value != null).length;

        return {
          values,
          selectedCount,
          complete: groups.length > 0 && selectedCount === groups.length,
        };
      }

      getVariantIndex() {
        const index = this.querySelector('[data-restock-variant-index]');
        if (!index) return [];

        try {
          return JSON.parse(index.textContent);
        } catch (error) {
          return [];
        }
      }

      hasUnavailableMatch(selectedValues) {
        return this.variantIndex.some(
          (variant) =>
            variant.available === false &&
            selectedValues.every(
              (selectedValue, optionIndex) =>
                selectedValue == null || variant.options[optionIndex] === selectedValue
            )
        );
      }

      hasUnavailablePartialMatch(selectedValues) {
        const selectedIndexes = selectedValues
          .map((value, index) => (value == null ? null : index))
          .filter((index) => index != null);

        return selectedIndexes.some((excludedIndex) =>
          this.hasUnavailableMatch(
            selectedValues.map((value, index) => (index === excludedIndex ? null : value))
          )
        );
      }

      clearVariantState() {
        this.activeVariantId = '';
        this.closePanel();
        if (this.variantInput) this.variantInput.value = '';
        if (this.skuInput) this.skuInput.value = '';
        if (this.emailNote) this.emailNote.hidden = true;
        if (this.promptAction) this.promptAction.hidden = true;
        if (this.toggle) {
          this.toggle.disabled = true;
          this.toggle.textContent = this.dataset.promptButtonLabel;
        }
        if (this.loginLink) {
          this.loginLink.textContent = this.dataset.promptButtonLabel;
          this.loginLink.removeAttribute('href');
          this.loginLink.setAttribute('aria-disabled', 'true');
          this.loginLink.setAttribute('tabindex', '-1');
        }
        if (this.switchAccountLink) this.switchAccountLink.removeAttribute('href');
      }

      setHiddenState() {
        this.clearVariantState();
        this.hidden = true;
      }

      preserveInitialActiveState() {
        this.activeVariantId = this.variantInput?.value || '';
        this.hidden = false;
      }

      setPromptState() {
        if (this.dataset.hasUnavailableVariant !== 'true') {
          this.setHiddenState();
          return;
        }

        this.clearVariantState();
        this.hidden = false;
        if (this.promptAction) this.promptAction.hidden = false;
      }

      setActiveState(variant) {
        if (!variant?.id || variant.available !== false) {
          this.setHiddenState();
          return;
        }

        this.clearVariantState();
        this.activeVariantId = String(variant.id);
        this.hidden = false;
        if (this.emailNote) this.emailNote.hidden = false;
        if (this.promptAction) this.promptAction.hidden = true;
        if (this.toggle) {
          this.toggle.disabled = false;
          this.toggle.textContent = this.dataset.buttonLabel;
        }
        if (this.loginLink) {
          this.loginLink.textContent = this.dataset.signInButtonLabel;
          this.loginLink.href = this.buildLoginUrl(variant.id);
          this.loginLink.removeAttribute('aria-disabled');
          this.loginLink.removeAttribute('tabindex');
        }
        if (this.variantInput) this.variantInput.value = variant.id;
        if (this.skuInput) this.skuInput.value = variant.sku || '';
        if (this.switchAccountLink) this.switchAccountLink.href = this.buildSwitchAccountUrl(variant.id);
      }

      resetSubscriptionState() {
        this.setStatus('');
        if (this.form) {
          this.form.hidden = false;
          const submitButton = this.form.querySelector('[type="submit"]');
          if (submitButton) submitButton.disabled = false;
        }
      }

      buildReturnTo(variantId) {
        const params = new URLSearchParams();
        params.set('variant', variantId);
        params.set('restock_signup', '1');
        return `${window.location.pathname}?${params.toString()}`;
      }

      buildLoginUrl(variantId) {
        const base = this.dataset.loginUrl || '/customer_authentication/login';
        const loginUrl = new URL(base, window.location.origin);
        loginUrl.searchParams.set('return_to', this.buildReturnTo(variantId));
        return `${loginUrl.pathname}${loginUrl.search}`;
      }

      buildSwitchAccountUrl(variantId) {
        const base = this.dataset.logoutUrl || '/account/logout';
        const logoutUrl = new URL(base, window.location.origin);
        logoutUrl.searchParams.set('return_url', this.buildLoginUrl(variantId));
        return `${logoutUrl.pathname}${logoutUrl.search}`;
      }

      handleReturnMarker() {
        const params = new URLSearchParams(window.location.search);
        if (params.get('restock_signup') !== '1') return;
        params.delete('restock_signup');
        const query = params.toString();
        history.replaceState(
          null,
          '',
          window.location.pathname + (query ? `?${query}` : '') + window.location.hash
        );
        if (this.toggle && this.activeVariantId) {
          this.openPanel();
          this.scrollIntoView({ block: 'center' });
          const submitButton = this.form && this.form.querySelector('[type="submit"]');
          if (submitButton) submitButton.focus();
        }
      }

      openPanel() {
        if (!this.panel) return;
        this.panel.hidden = false;
        if (this.toggle) this.toggle.setAttribute('aria-expanded', 'true');
      }

      closePanel() {
        if (this.panel) this.panel.hidden = true;
        if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');
      }

      onToggleClick() {
        if (!this.panel) return;
        const expand = this.panel.hidden;
        this.panel.hidden = !expand;
        this.toggle.setAttribute('aria-expanded', expand ? 'true' : 'false');
      }

      setStatus(message) {
        if (this.status) this.status.textContent = message;
      }

      setStatusFromTemplate(template) {
        if (!this.status) return;
        this.status.replaceChildren();
        if (template) this.status.append(template.content.cloneNode(true));
      }

      onSubmit(event) {
        event.preventDefault();
        if (
          !this.activeVariantId ||
          this.variantInput?.value !== this.activeVariantId ||
          (this.isPickAnOptionEnabled() && !this.getOptionSelection().complete)
        ) {
          if (this.isPickAnOptionEnabled()) {
            this.updatePickAnOptionState();
          } else {
            this.setPromptState();
          }
          return;
        }
        const submitButton = this.form.querySelector('[type="submit"]');
        if (submitButton) submitButton.disabled = true;
        this.setStatus('');

        fetch(this.dataset.subscribeUrl, {
          method: 'POST',
          body: new FormData(this.form),
          headers: { Accept: 'application/json' },
        })
          .then(async (response) => {
            let payload = {};
            try {
              payload = await response.json();
            } catch (error) {
              payload = {};
            }
            if (payload.status === 'subscribed' || payload.status === 'already_subscribed') {
              this.form.hidden = true;
              this.setStatusFromTemplate(
                payload.status === 'subscribed'
                  ? this.subscribedMessage
                  : this.alreadySubscribedMessage
              );
              return;
            }
            if (submitButton) submitButton.disabled = false;
            this.setStatus(
              payload.status === 'login_required'
                ? this.dataset.textLoginRequired
                : this.dataset.textError
            );
          })
          .catch(() => {
            if (submitButton) submitButton.disabled = false;
            this.setStatus(this.dataset.textError);
          });
      }
    }
  );
}
